/* eslint-disable */
import {
  addHours,
  addMinutes,
  format,
  getHours,
  getMinutes,
  setHours,
  setMinutes,
  subHours,
  subMinutes,
} from 'date-fns'
import React, { useEffect } from 'react'
import Button from '../../Template/Button'
import Popover from '../../Template/Popover'
import { IFormElementProps } from '../FormElement/FormInput'
import Control from './Components/Control'
import Separator from './Components/Separator'
import TimeBlock from './Components/TimeBlock'
import TimePickerWrapper from './Components/TimePickerWrapper'

function TimePicker({
  value = new Date(),
  onChange,
  close,
  anchor,
  expand,
  h12,
  disabled,
  onlyMinutes = false,
  disallowZero = false,
}: ITimePickerProps & ITimePickerDispatch) {
  const [expandHours, setExpandHours] = React.useState(false)
  const [expandMinutes, setExpandMinutes] = React.useState(false)

  function onClose() {
    setExpandHours(false)
    setExpandMinutes(false)
    close()
  }

  useEffect(() => {
    if (disallowZero && value.getMinutes() === 0) {
      value = addMinutes(value, 1)
      formatDate()
    }
  }, [disallowZero, value])

  const minute = format(value, 'mm')
  const hour = format(value, h12 ? 'hh' : 'HH')
  const AMPM = format(value, 'A')

  function formatDate() {
    const h = getHours(value)
    const m = getMinutes(value)
    onChange(
      `${onlyMinutes ? '00' : h < 10 ? `0${h}` : h}:${m < 10 ? `0${m}` : m}`,
    )
  }

  function onBlockClick(type) {
    return function (h, m) {
      return function () {
        setExpandHours(false)
        setExpandMinutes(false)
        if (type === 'minutes') {
          value = setHours(setMinutes(value, m), h)
        } else {
          if (h < 12 && h12) {
            value = setHours(
              setMinutes(value, m),
              getHours(
                addHours(setHours(new Date(), h), AMPM === 'PM' ? 12 : 0),
              ),
            )
          } else if (h === 12 && h12) {
            if (AMPM === 'PM') {
              value = setHours(
                setMinutes(value, m),
                getHours(setHours(new Date(), 12)),
              )
            } else {
              value = setHours(
                setMinutes(value, m),
                getHours(setHours(new Date(), 0)),
              )
            }
          } else {
            value = setHours(
              setMinutes(value, m),
              getHours(setHours(new Date(), h)),
            )
          }
        }
        formatDate()
      }
    }
  }

  function toggleHours() {
    setExpandHours(!expandHours)
  }

  function toggleMinutes() {
    setExpandMinutes(!expandMinutes)
  }

  function toggleFormat() {
    if (AMPM === 'AM') {
      value = addHours(value, 12)
      formatDate()
    } else if (AMPM === 'PM') {
      value = subHours(value, 12)
      formatDate()
    }
  }

  function displayHours() {
    const hours = h12
      ? [
          <TimeBlock
            key={`timeblock-h-${12}`}
            onClick={onBlockClick('hours')(12, Number(minute))}
            sized={true}
          >
            {12}
          </TimeBlock>,
        ]
      : []
    for (let i = h12 ? 1 : 0; i <= (h12 ? 11 : 23); i++) {
      hours.push(
        <TimeBlock
          onClick={onBlockClick('hours')(i, Number(minute))}
          sized={true}
          key={`timeblock-h-${i}`}
          style={h12 ? {} : { maxHeight: '30px' }}
        >
          {i < 10 ? `0${i}` : i}
        </TimeBlock>,
      )
    }
    return hours
  }

  function displayMinutes() {
    const minutes = []
    for (let i = 0; i <= 59; i += 5) {
      minutes.push(
        <TimeBlock
          style={
            disallowZero && i === 0
              ? { opacity: 0.6, pointerEvents: 'none' }
              : {}
          }
          onClick={onBlockClick('minutes')(Number(getHours(value)), i)}
          sized={true}
        >
          {i < 10 ? `0${i}` : i}
        </TimeBlock>,
      )
    }
    return minutes
  }

  function increment(type: string) {
    return function () {
      switch (type) {
        case 'hours':
          value = addHours(value, 1)
          break
        case 'minutes':
          if (disallowZero && value.getMinutes() === 59) return
          value = addMinutes(value, 1)
          break
      }
      formatDate()
    }
  }

  function decrement(type: string) {
    return function () {
      switch (type) {
        case 'hours':
          value = subHours(value, 1)
          break
        case 'minutes':
          if (disallowZero && value.getMinutes() === 1) return
          value = subMinutes(value, 1)
          break
      }
      formatDate()
    }
  }

  return (
    <Popover
      open={expand}
      onClose={onClose}
      anchorEl={anchor.current}
      anchorOrigin={{
        horizontal: 'center',
        vertical: 'bottom',
      }}
      transformOrigin={{
        horizontal: 'center',
        vertical: 'top',
      }}
      withArrow={false}
    >
      <TimePickerWrapper oneColumn={onlyMinutes && !expandMinutes}>
        {!expandHours && !expandMinutes && (
          <React.Fragment>
            {!onlyMinutes && (
              <>
                <Control
                  onClick={toggleHours}
                  value={h12 ? Number(hour) : hour}
                  increment={increment('hours')}
                  decrement={decrement('hours')}
                />
                <Separator>:</Separator>
              </>
            )}
            <Control
              onClick={toggleMinutes}
              value={minute}
              increment={increment('minutes')}
              decrement={decrement('minutes')}
            />
            {h12 && !onlyMinutes && (
              <Button
                primary={true}
                style={{ fontSize: '12px' }}
                onClick={toggleFormat}
              >
                {AMPM}
              </Button>
            )}
          </React.Fragment>
        )}

        {expandHours && displayHours()}
        {expandMinutes && displayMinutes()}
      </TimePickerWrapper>
    </Popover>
  )
}

export default TimePicker

export interface ITimePickerProps extends IFormElementProps {
  value?: Date
  anchor?: React.MutableRefObject<HTMLElement | HTMLInputElement>
  expand?: boolean
  h12?: boolean
  onlyMinutes?: boolean
  disabled?: boolean
  disallowZero?: boolean
}

export interface ITimePickerDispatch {
  onChange: (val: string) => void
  close?: () => void
}
